import { useState, useCallback, useEffect } from 'react';
import { uploadFile, deleteFile } from '../api';
import { TFile, TUploadedFile, TFileUploadError, Errors, FileUploaderErrorCodes } from '../types';
import { stripText } from '../helpers/textsHelpers';

export const useFileUpload = (
  item: TFile,
  errorTexts: Errors,
  setAppendDocument: (fileUploadId: string, filename: string) => void,
  removeFileUploadId: (fileUploadId: string) => void,
  removeFile: (id: string) => void,
) => {
  // Local states
  const [fileUploadError, setFileUploadError] = useState<TFileUploadError | null>(null);
  const [fileUploadPercentage, setFileUploadPercentage] = useState<number>(0);
  const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
  const [uploadedFileList, setUploadedFileList] = useState<TUploadedFile[] | null>(null);

  useEffect(() => {
    handleFileUpload(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = useCallback(
    async (item: TFile | null) => {
      // Failed at client-side validation check.
      if (item?.errorCode && item?.errorMessage) {
        setFileUploadError({
          errorCode: item?.errorCode,
          errorMessage: item?.errorMessage,
        });
        return;
      }

      if (item && item.file) {
        setFileUploadError(null);
        setIsFileUploaded(false);
        try {
          const response = await uploadFile(item.file, {
            onUploadProgress: setFileUploadPercentage,
          });
          if (response.status === 201 && response.data) {
            setIsFileUploaded(true);
            const newUploadFile: TUploadedFile = {
              itemId: item.id,
              uploadId: response.data.fileHandle,
            };
            setUploadedFileList([...(uploadedFileList || []), newUploadFile]);
            setAppendDocument(response.data.fileHandle, item.file.name);
          }
        } catch (e: any) {
          if (e.response?.status === 400) {
            setFileUploadError({
              errorCode: FileUploaderErrorCodes.BAD_REQUEST,
              errorMessage: stripText(errorTexts.badRequestError),
            });
          } else {
            setFileUploadError({
              errorCode: FileUploaderErrorCodes.NETWORK,
              errorMessage: stripText(errorTexts.networkError),
            });
          }
        } finally {
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAppendDocument],
  );

  const handleFileRemoveClick = useCallback(async () => {
    removeFile(item.id);
    if (isFileUploaded) {
      const matchedUploadedFile = uploadedFileList?.find((x) => x.itemId === item.id);
      if (matchedUploadedFile) {
        const { uploadId } = matchedUploadedFile;
        try {
          await deleteFile(uploadId);
        } catch (error) {
        } finally {
          removeFileUploadId(uploadId);
        }
      }
    }
  }, [isFileUploaded, item.id, removeFile, removeFileUploadId, uploadedFileList]);

  return {
    isFileUploaded,
    fileUploadError,
    fileUploadPercentage,
    handleFileUpload,
    handleFileRemoveClick,
  };
};
